import type { TiptapDoc } from '@odo/shared-ui';
import apiV2 from '../api/v2';
import { parseBlobExportError } from '../utils/blobError';

export interface ChargeListItem {
    id: string;
    practiceId: string;
    practiceName: string;
    practiceLocation: string | null;
    practiceDoctorName: string | null;
    patientId: string | null;
    patientName: string | null;
    description: string | null;
    productSummary: string | null;
    oneTimeDiscountsTotal: string;
    // One-time discounts that name this charge as their source (already carried forward).
    issuedOneTimeDiscountsCount: number;
    baseAmount: string;
    discountType: string | null;
    discountValue: string | null;
    totalAmount: string;
    installments: number;
    paymentTerms: number[];
    status: string;
    createdVia: string;
    lineItemCount: number;
    createdAt: string;
    updatedAt: string;
}

export interface ChargeLineItem {
    id: string;
    productVersionId: string;
    versionLabel: string;
    // The base product the charged version belongs to.
    productId: string;
    productName: string;
    description: string;
    unitPrice: string;
    quantity: number;
    lineTotal: string;
}

export interface InvoiceListItem {
    id: string;
    chargeId: string;
    practiceId: string;
    practiceName: string;
    practiceDoctorName: string | null;
    patientId: string | null;
    patientName: string | null;
    chargeDescription: string | null;
    // Total installment count on the parent charge, for "1 of 3" labels.
    installments: number;
    invoiceNumber: string;
    installmentIndex: number;
    amount: string;
    currency: string;
    dueDate: string;
    billcomId: string | null;
    status: string;
    paidAt: string | null;
    createdAt: string;
}

export interface InvoiceDetail {
    id: string;
    chargeId: string;
    charge: {
        id: string;
        description: string | null;
        productSummary: string | null;
        totalAmount: string;
        installments: number;
        status: string;
        practice: {
            id: string;
            name: string;
            location: string | null;
            doctorName: string | null;
        };
        patientId: string | null;
        patientName: string | null;
    };
    invoiceNumber: string;
    installmentIndex: number;
    amount: string;
    currency: string;
    dueDate: string;
    billcomId: string | null;
    status: string;
    paidAt: string | null;
    events: ChargeEventItem[];
    createdAt: string;
    updatedAt: string;
}

export interface InvoiceStatusSummary {
    count: number;
    totalAmount: string;
}

export interface InvoicesListParams {
    page: number;
    limit: number;
    q?: string;
    status?: string;
    practiceId?: string;
    sort?: 'due' | 'amount' | 'date' | 'patient';
    order?: 'asc' | 'desc';
}

export interface InvoicesListMeta {
    total: number;
    page: number;
    limit: number;
    statusSummary?: Record<string, InvoiceStatusSummary>;
}

// Paid and void invoices are history; everything else can still be voided.
export function isInvoiceVoidable(status: string): boolean {
    return status !== 'paid' && status !== 'void';
}

export interface VoidInvoiceBody {
    reason: string;
    // When set, voiding also issues an unlinked one-time discount for the practice.
    createOneTimeDiscount?: {
        // Defaults to the invoice amount when omitted.
        amount?: number;
        description: string;
        expiresAt?: string;
    };
}

export interface PurchaseOrderItem {
    id: string;
    // Provenance only - the charge this PO was raised from. Never attribute
    // cost or group totals by it; the PO's lines are what carry cost.
    originatingChargeId: string;
    // Slug; vendorName is the catalogue's display name for it.
    vendor: string;
    vendorName: string;
    poNumber: string;
    documentFileId: string | null;
    sentTo: string[];
    // Always set: a purchase order row only exists because it was dispatched.
    sentAt: string;
    error: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface ChargeOneTimeDiscountItem {
    id: string;
    chargeId: string | null;
    sourceChargeId: string | null;
    sourceCharge: { id: string; description: string | null } | null;
    amount: string;
    description: string;
    expiresAt: string | null;
    createdByUserId: string | null;
    createdAt: string;
}

export interface ChargeEventItem {
    id: string;
    entityType: string;
    eventType: string;
    actor: string;
    actorName: string | null;
    payload: unknown;
    occurredAt: string;
}

export interface ChargeDetail {
    id: string;
    practiceId: string;
    practice: {
        id: string;
        name: string;
        location: string | null;
        doctorName: string | null;
    };
    patientId: string | null;
    patientName: string | null;
    createdByName: string | null;
    description: string | null;
    baseAmount: string;
    discountType: string | null;
    discountValue: string | null;
    totalAmount: string;
    installments: number;
    paymentTerms: number[];
    status: string;
    error: string | null;
    createdVia: string;
    createdByUserId: string | null;
    lineItems: ChargeLineItem[];
    invoiceTotal: string;
    invoices: InvoiceListItem[];
    purchaseOrders: PurchaseOrderItem[];
    oneTimeDiscounts: ChargeOneTimeDiscountItem[];
    events: ChargeEventItem[];
    createdAt: string;
    updatedAt: string;
}

export interface LineItemInput {
    // Existing line id: present updates that line, absent inserts a new one.
    id?: string;
    // A product VERSION id — the version carries the price and Bill.com item.
    productVersionId: string;
    description: string;
    unitPrice: number;
    quantity: number;
}

export interface CreateChargeBody {
    practiceId: string;
    patientId?: string;
    description?: string;
    lineItems: LineItemInput[];
    discountType?: 'flat' | 'percentage';
    discountValue?: number;
    installments: number;
    paymentTerms: number[];
    // Only meaningful when a line item is the OrthoPulse product - see
    // isOrthopulseProductName. Charge creation never fails because of this.
    sendPurchaseOrder?: boolean;
}

export interface ChargePurchaseOrderResult {
    sent: true;
    poNumber: string;
    recipients: string[];
    vendor: string;
}

export interface ChargePurchaseOrderNotSent {
    sent: false;
    reason: string;
}

export interface BillingCharge {
    id: string;
    practiceId: string;
    status: string;
    totalAmount: string;
    installments: number;
    paymentTerms: number[];
    // Present only when sendPurchaseOrder was requested on creation.
    purchaseOrder?: ChargePurchaseOrderResult | ChargePurchaseOrderNotSent;
    createdAt: string;
    updatedAt: string;
}

// A sellable variation of a base product: its own price and Bill.com item.
// `selectors` are the practice-matching criteria ({} = every practice).
export interface ProductVersion {
    id: string;
    label: string;
    selectors: Record<string, string>;
    priceCents: number;
    // null = not tracked (no vendor purchase order for this version).
    vendorCostCents: number | null;
    billId: string | null;
    // Vendor part number used by the webstore (e.g. 18LTAM). null = not a webstore item.
    vendorPartNumber: string | null;
    // Units per sellable box (DM: 1 box = 3 adapters). null = sold by the unit.
    packSize: number | null;
    // null = no purchase order is raised when this version is charged. Must
    // belong to the owning product's vendor.
    orderMethod: ProductOrderMethodRef | null;
    // ISO instant; from it on the version is never picked for new charges,
    // prices or orders. null = no expiry.
    expiresAt: string | null;
    // Billing's pick among a practice's live versions when the Dx report names none.
    isDefault: boolean;
}

// A discount rule on a base product; practices are made eligible to it.
// Rules stack (percentages compound first, then flat amounts).
export interface ProductDiscountRule {
    id: string;
    name: string;
    discountType: 'flat' | 'percentage';
    discountValue: number;
    // null = the rule applies to every version of the product
    appliesToVersionId: string | null;
    // null bounds = always active
    startsAt: string | null;
    endsAt: string | null;
    note: string | null;
    eligibleCount: number;
}

export interface ProductOrderMethodRef {
    id: string;
    name: string;
    kind: 'email' | 'external';
    isActive: boolean;
}

export interface ProductRow {
    id: string;
    name: string;
    description: string | null;
    category: string | null;
    subCategory: string | null;
    // Legacy free-text vendor; vendorId/vendorName are the catalogue's.
    vendor: string | null;
    vendorId: string | null;
    vendorName: string | null;
    // Whether the vendor has any ordering method to assign. Each version
    // carries its own orderMethod - see ProductVersion.
    vendorHasOrderMethods: boolean;
    // Retirement instant (ISO): past it the product is hidden from charge and
    // pricing pickers and cannot be charged. null = active.
    expiresAt: string | null;
    // Merchandising (webstore). marketingCopy is derived server-side from
    // marketingCopyDoc - read-only here, never sent back.
    imageFileId: string | null;
    marketingCopy: string | null;
    marketingCopyDoc: TiptapDoc | null;
    // Storefront gate: only these products are offered in the webstore.
    sellableInWebstore: boolean;
    versions: ProductVersion[];
    discountRules: ProductDiscountRule[];
    createdAt: string;
}

// Server-owned so the offered lists change without a frontend release; the API
// still accepts deprecated values no longer offered here.
export interface ProductTaxonomy {
    categories: string[];
    vendors: string[];
}

// PATCH body: omitted fields are left untouched; null clears a nullable field.
// Price and Bill.com id live on versions — see the version endpoints below.
export interface UpdateProductBody {
    name?: string;
    description?: string | null;
    category?: string | null;
    subCategory?: string | null;
    vendor?: string | null;
    vendorId?: string | null;
    expiresAt?: string | null;
    imageFileId?: string | null;
    marketingCopyDoc?: TiptapDoc | null;
    sellableInWebstore?: boolean;
}

export interface VersionBody {
    label: string;
    selectors: Record<string, string>;
    priceCents: number;
    vendorCostCents: number | null;
    billId: string | null;
    vendorPartNumber: string | null;
    packSize: number | null;
    // null clears (no PO raised). Must belong to the owning product's vendor.
    orderMethodId: string | null;
    expiresAt: string | null;
    isDefault?: boolean;
}

export interface CreateProductBody {
    name: string;
    description?: string | null;
    category?: string | null;
    subCategory?: string | null;
    vendor?: string | null;
    vendorId?: string | null;
    imageFileId?: string | null;
    marketingCopyDoc?: TiptapDoc | null;
    sellableInWebstore?: boolean;
    // Every product has at least one version. Ordering method lives on each.
    versions: VersionBody[];
}

// Create/update payload for a discount rule. practiceIds is the wholesale
// eligibility roster (omit on update to leave it untouched).
export interface DiscountRuleBody {
    name: string;
    discountType: 'flat' | 'percentage';
    discountValue: number;
    appliesToVersionId: string | null;
    startsAt: string | null;
    endsAt: string | null;
    note: string | null;
    practiceIds: string[];
}

// Practice-side view: every rule in the catalog with this practice's
// eligibility flag.
export interface PracticeDiscountRule {
    id: string;
    productId: string;
    productName: string;
    name: string;
    discountType: 'flat' | 'percentage';
    discountValue: number;
    appliesToVersionId: string | null;
    appliesToVersionLabel: string | null;
    startsAt: string | null;
    endsAt: string | null;
    active: boolean;
    eligible: boolean;
}

export interface PracticeOption {
    id: string;
    name: string;
    doctorName: string | null;
}

export interface ChargesListParams {
    page: number;
    limit: number;
    q?: string;
    practiceId?: string;
    patientId?: string;
    status?: string;
    createdVia?: string;
    sort?: 'practice' | 'patient' | 'total' | 'status' | 'date';
    order?: 'asc' | 'desc';
}

export interface ChargesListMeta {
    total: number;
    page: number;
    limit: number;
    statusCounts?: Record<string, number>;
}

export interface ProcessChargeResult {
    chargeRef: string;
    invoiceCount: number;
    failCount: number;
}

/** Backend-enforced ceiling on `search` for the charges, invoices and one-time discount lists. */
export const BILLING_LIST_SEARCH_MAX_LENGTH = 200;

export const fetchCharges = async (
    params: ChargesListParams
): Promise<{ data: ChargeListItem[]; meta: ChargesListMeta }> => {
    const query: Record<string, string> = {
        page: String(params.page),
        limit: String(params.limit)
    };
    // Canonical wire spelling; the endpoints also still accept the legacy `q`.
    if (params.q) query.search = params.q;
    if (params.practiceId) query.practiceId = params.practiceId;
    if (params.patientId) query.patientId = params.patientId;
    if (params.status) query.status = params.status;
    if (params.createdVia) query.createdVia = params.createdVia;
    if (params.sort) query.sort = params.sort;
    if (params.order) query.order = params.order;

    const response = await apiV2.get('/billing/charges', { params: query });
    return response.data;
};

export const fetchCharge = async (id: string): Promise<ChargeDetail> => {
    const response = await apiV2.get<{ success: boolean; data: ChargeDetail }>(
        `/billing/charges/${id}`
    );
    return response.data.data;
};

export const createCharge = async (body: CreateChargeBody): Promise<BillingCharge> => {
    const response = await apiV2.post<{ success: boolean; data: BillingCharge }>(
        '/billing/charges',
        body
    );
    return response.data.data;
};

export const processCharge = async (id: string): Promise<ProcessChargeResult> => {
    const response = await apiV2.post<{ success: boolean; data: ProcessChargeResult }>(
        `/billing/charges/${id}/process`
    );
    return response.data.data;
};

export const cancelCharge = async (id: string, reason: string): Promise<void> => {
    await apiV2.post(`/billing/charges/${id}/cancel`, { reason });
};

// PATCH semantics; only pending charges accept edits. null clears;
// lineItems upsert by id: an id updates that line, no id inserts, an omitted line is deleted.
export interface UpdateChargeChanges {
    patientId?: string | null;
    description?: string | null;
    lineItems?: LineItemInput[];
    discountType?: 'flat' | 'percentage' | null;
    discountValue?: number | null;
    installments?: number;
    paymentTerms?: number[];
}

export const updateCharge = async (
    id: string,
    changes: UpdateChargeChanges
): Promise<ChargeDetail> => {
    const response = await apiV2.patch<{ success: boolean; data: ChargeDetail }>(
        `/billing/charges/${id}`,
        changes
    );
    return response.data.data;
};

// Replaces every credit linked to the charge in one transaction: all or nothing.
export const setChargeOneTimeDiscounts = async (
    chargeId: string,
    oneTimeDiscountIds: string[]
): Promise<{ oneTimeDiscountIds: string[] }> => {
    const response = await apiV2.put<{ success: boolean; data: { oneTimeDiscountIds: string[] } }>(
        `/billing/charges/${chargeId}/one-time-discounts`,
        { oneTimeDiscountIds }
    );
    return response.data.data;
};

export const retryCharge = async (id: string): Promise<ProcessChargeResult> => {
    const response = await apiV2.post<{ success: boolean; data: ProcessChargeResult }>(
        `/billing/charges/${id}/retry`
    );
    return response.data.data;
};

export interface OverviewKpis {
    invoiced: string;
    invoicedDeltaPct: number | null;
    collected: string;
    collectionRatePct: number | null;
    outstanding: string;
    outstandingCount: number;
    avgDaysToPay: number | null;
    avgDaysToPayDelta: number | null;
    oneTimeDiscountsIssued: string;
    oneTimeDiscountsApplied: string;
}

export interface OverviewMonthlyPoint {
    month: string; // YYYY-MM
    paid: string;
    open: string;
    overdue: string;
    void: string;
}

export interface OverviewCollectionPoint {
    month: string;
    ratePct: number | null;
    collected: string;
}

export interface OverviewAgingBucket {
    bucket: 'current' | '1-30' | '31-60' | '61-90' | '90+';
    amount: string;
    count: number;
}

export interface OverviewCategorySlice {
    category: string;
    amount: string;
}

export interface OverviewTopPractice {
    practiceId: string;
    name: string;
    doctorName: string | null;
    amount: string;
    invoices: number;
}

// Snapshot as of today (like the outstanding KPI), not a range aggregate.
export interface OverviewTopDebtor {
    practiceId: string;
    name: string;
    doctorName: string | null;
    amount: string;
    overdue: string;
    invoices: number;
}

export interface OverviewAttentionItem {
    kind: 'error' | 'expiring' | 'pending';
    title: string;
    sub: string;
    entityType: 'charge' | 'invoice' | 'one_time_discount';
    entityId: string;
}

export interface OverviewActivityItem {
    id: string;
    eventType: string;
    entityType: string;
    entityId: string;
    entityRef: string | null;
    actor: string;
    actorName: string | null;
    occurredAt: string;
}

export interface BillingOverview {
    kpis: OverviewKpis;
    // Same series bucketed by due month and by creation month.
    monthly: { due: OverviewMonthlyPoint[]; created: OverviewMonthlyPoint[] };
    collections: OverviewCollectionPoint[];
    aging: OverviewAgingBucket[];
    byCategory: OverviewCategorySlice[];
    topPractices: OverviewTopPractice[];
    topDebtors: OverviewTopDebtor[];
    needsAttention: OverviewAttentionItem[];
    recent: OverviewActivityItem[];
}

// from = null means all time. practiceId scopes every aggregation to one
// practice (topPractices/topDebtors then hold just that row); omitted = org-wide.
export const fetchBillingOverview = async (
    from: string | null,
    to: string,
    practiceId?: string
): Promise<BillingOverview> => {
    const response = await apiV2.get<{ success: boolean; data: BillingOverview }>(
        '/billing/overview',
        {
            params: { ...(from ? { from } : {}), to, ...(practiceId ? { practiceId } : {}) }
        }
    );
    return response.data.data;
};

export const fetchInvoices = async (
    params: InvoicesListParams
): Promise<{ data: InvoiceListItem[]; meta: InvoicesListMeta }> => {
    const query: Record<string, string> = {
        page: String(params.page),
        limit: String(params.limit)
    };
    // Canonical wire spelling; the endpoints also still accept the legacy `q`.
    if (params.q) query.search = params.q;
    if (params.status) query.status = params.status;
    if (params.practiceId) query.practiceId = params.practiceId;
    if (params.sort) query.sort = params.sort;
    if (params.order) query.order = params.order;

    const response = await apiV2.get('/billing/invoices', { params: query });
    return response.data;
};

export interface InvoicesExportParams {
    /** YYYY-MM-DD creation-date bounds; the server defaults to the last 3 months. */
    from?: string;
    to?: string;
    practiceId?: string;
    patientId?: string;
}

export interface InvoicesExportResult {
    blob: Blob;
    /** The server capped the row count; the range needs narrowing. */
    truncated: boolean;
}

/** A failure body arrives as a Blob (responseType), parsed back to JSON first. */
export const exportInvoicesCsv = async (
    params: InvoicesExportParams
): Promise<InvoicesExportResult> => {
    try {
        const response = await apiV2.get<Blob>('/billing/invoices/export', {
            params: {
                ...(params.from ? { from: params.from } : {}),
                ...(params.to ? { to: params.to } : {}),
                ...(params.practiceId ? { practiceId: params.practiceId } : {}),
                ...(params.patientId ? { patientId: params.patientId } : {})
            },
            responseType: 'blob'
        });
        return {
            blob: response.data,
            truncated: response.headers['x-export-truncated'] === 'true'
        };
    } catch (error) {
        throw await parseBlobExportError(error);
    }
};

export const fetchInvoice = async (id: string): Promise<InvoiceDetail> => {
    const response = await apiV2.get<{ success: boolean; data: InvoiceDetail }>(
        `/billing/invoices/${id}`
    );
    return response.data.data;
};

export const voidInvoice = async (
    id: string,
    body: VoidInvoiceBody
): Promise<{ oneTimeDiscountId: string | null }> => {
    const response = await apiV2.post<{
        success: boolean;
        data: { oneTimeDiscountId: string | null };
    }>(`/billing/invoices/${id}/void`, body);
    return response.data.data;
};

export const fetchChargeInvoices = async (chargeId: string): Promise<InvoiceListItem[]> => {
    const response = await apiV2.get<{
        success: boolean;
        data: InvoiceListItem[];
        meta: { total: number };
    }>('/billing/invoices', {
        params: { chargeId }
    });
    return response.data.data;
};

export type OneTimeDiscountStatus = 'available' | 'expiring' | 'applied' | 'expired';

export interface OneTimeDiscountListItem {
    id: string;
    chargeId: string | null;
    // Status of the charge the one-time discount is applied to; unlink is only
    // possible while that charge is pending or canceled.
    chargeStatus: string | null;
    sourceChargeId: string | null;
    sourceChargeDescription: string | null;
    amount: string;
    description: string;
    expiresAt: string | null;
    createdAt: string;
    practiceId: string | null;
    practiceName: string | null;
    practiceDoctorName: string | null;
    // Whose credit it is: the source charge's patient, else the applied charge's.
    patientId: string | null;
    patientName: string | null;
    status: OneTimeDiscountStatus;
}

export interface OneTimeDiscountStatusSummary {
    count: number;
    totalAmount: string;
}

export interface OneTimeDiscountsListParams {
    page: number;
    limit: number;
    q?: string;
    status?: OneTimeDiscountStatus[];
    practiceId?: string;
    // Credits of this patient (ownership comes from the issuing charge).
    patientId?: string;
    // Also return the credits that belong to no patient. On its own, narrows
    // the list to just those.
    includeUnassigned?: boolean;
    // false: only credits not yet linked to any charge.
    linked?: boolean;
    sort?: 'expiry' | 'date' | 'patient';
    order?: 'asc' | 'desc';
}

export interface OneTimeDiscountsListMeta {
    total: number;
    page: number;
    limit: number;
    statusSummary?: Record<string, OneTimeDiscountStatusSummary>;
}

export interface CreateOneTimeDiscountBody {
    amount: number;
    description: string;
    expiresAt?: string;
    // The practice the one-time discount belongs to; derived from the charges when omitted.
    practiceId?: string;
    sourceChargeId?: string;
    // Optional link target: attaches the one-time discount to this charge at creation.
    chargeId?: string;
}

export const fetchOneTimeDiscounts = async (
    params: OneTimeDiscountsListParams
): Promise<{ data: OneTimeDiscountListItem[]; meta: OneTimeDiscountsListMeta }> => {
    const query: Record<string, string | string[]> = {
        page: String(params.page),
        limit: String(params.limit)
    };
    // Canonical wire spelling; the endpoints also still accept the legacy `q`.
    if (params.q) query.search = params.q;
    if (params.status?.length) query.status = params.status;
    if (params.practiceId) query.practiceId = params.practiceId;
    if (params.patientId) query.patientId = params.patientId;
    if (params.includeUnassigned) query.includeUnassigned = 'true';
    if (params.linked !== undefined) query.linked = String(params.linked);
    if (params.sort) query.sort = params.sort;
    if (params.order) query.order = params.order;

    const response = await apiV2.get('/billing/one-time-discounts', { params: query });
    return response.data;
};

export const createOneTimeDiscount = async (
    body: CreateOneTimeDiscountBody
): Promise<{ id: string }> => {
    const response = await apiV2.post<{ success: boolean; data: { id: string } }>(
        '/billing/one-time-discounts',
        body
    );
    return response.data.data;
};

export const linkOneTimeDiscount = async (id: string, chargeId: string): Promise<void> => {
    await apiV2.post(`/billing/one-time-discounts/${id}/link`, { chargeId });
};

export const unlinkOneTimeDiscount = async (id: string): Promise<void> => {
    await apiV2.post(`/billing/one-time-discounts/${id}/unlink`);
};

export const deleteOneTimeDiscount = async (id: string): Promise<void> => {
    await apiV2.delete(`/billing/one-time-discounts/${id}`);
};

export const fetchProducts = async (params?: {
    sort?: 'name' | 'category' | 'date';
    order?: 'asc' | 'desc';
}): Promise<ProductRow[]> => {
    const response = await apiV2.get<{
        success: boolean;
        data: ProductRow[];
        meta: { total: number };
    }>('/products', {
        params: { page: 1, limit: 100, sort: params?.sort ?? 'name', order: params?.order ?? 'asc' }
    });
    return response.data.data;
};

// The options endpoint, not the paginated list: unbounded (a page cap silently
// hid practices from pickers) and free of joins a dropdown has no use for.
export const fetchPracticeOptions = async (): Promise<PracticeOption[]> => {
    const response = await apiV2.get<{ success: boolean; data: PracticeOption[] }>(
        '/practices/options'
    );
    return response.data.data;
};

export const fetchProductTaxonomy = async (): Promise<ProductTaxonomy> => {
    const response = await apiV2.get<{ success: boolean; data: ProductTaxonomy }>(
        '/products/taxonomy'
    );
    return response.data.data;
};

export const createProduct = async (body: CreateProductBody): Promise<ProductRow> => {
    const response = await apiV2.post<{ success: boolean; data: ProductRow }>('/products', body);
    return response.data.data;
};

export const updateProduct = async (id: string, body: UpdateProductBody): Promise<ProductRow> => {
    const response = await apiV2.patch<{ success: boolean; data: ProductRow }>(
        `/products/${id}`,
        body
    );
    return response.data.data;
};

export const createProductVersion = async (
    productId: string,
    body: VersionBody
): Promise<ProductVersion> => {
    const response = await apiV2.post<{ success: boolean; data: ProductVersion }>(
        `/products/${productId}/versions`,
        body
    );
    return response.data.data;
};

export const updateProductVersion = async (
    productId: string,
    productVersionId: string,
    body: Partial<VersionBody>
): Promise<ProductVersion> => {
    const response = await apiV2.patch<{ success: boolean; data: ProductVersion }>(
        `/products/${productId}/versions/${productVersionId}`,
        body
    );
    return response.data.data;
};

export const deleteProductVersion = async (
    productId: string,
    productVersionId: string
): Promise<void> => {
    await apiV2.delete(`/products/${productId}/versions/${productVersionId}`);
};

export const createDiscountRule = async (
    productId: string,
    body: DiscountRuleBody
): Promise<ProductDiscountRule> => {
    const response = await apiV2.post<{ success: boolean; data: ProductDiscountRule }>(
        `/products/${productId}/discount-rules`,
        body
    );
    return response.data.data;
};

export const updateDiscountRule = async (
    productId: string,
    ruleId: string,
    body: Partial<DiscountRuleBody>
): Promise<ProductDiscountRule> => {
    const response = await apiV2.patch<{ success: boolean; data: ProductDiscountRule }>(
        `/products/${productId}/discount-rules/${ruleId}`,
        body
    );
    return response.data.data;
};

export const deleteDiscountRule = async (productId: string, ruleId: string): Promise<void> => {
    await apiV2.delete(`/products/${productId}/discount-rules/${ruleId}`);
};

// Practice ids eligible for one rule - feeds the roster editor.
export const fetchDiscountRulePractices = async (
    productId: string,
    ruleId: string
): Promise<string[]> => {
    const response = await apiV2.get<{ success: boolean; data: string[] }>(
        `/products/${productId}/discount-rules/${ruleId}/practices`
    );
    return response.data.data;
};

export interface DiscountCoverage {
    // Practices holding at least one rule eligibility, vs all practices.
    covered: number;
    total: number;
}

export const fetchDiscountCoverage = async (): Promise<DiscountCoverage> => {
    const response = await apiV2.get<{ success: boolean; data: DiscountCoverage }>(
        '/products/discount-coverage'
    );
    return response.data.data;
};

export const fetchPracticeDiscountRules = async (
    practiceId: string
): Promise<PracticeDiscountRule[]> => {
    const response = await apiV2.get<{ success: boolean; data: PracticeDiscountRule[] }>(
        `/practices/${practiceId}/discount-rules`
    );
    return response.data.data;
};

export const setPracticeRuleEligibility = async (
    practiceId: string,
    ruleId: string,
    eligible: boolean
): Promise<void> => {
    await apiV2.put(`/practices/${practiceId}/discount-rules/${ruleId}`, { eligible });
};
